// ─────────────────────────────────────────────
// src/components/Nav.jsx
// Responsive navigation bar with hamburger menu
// for mobile and separate Staff / Parent login.
// ─────────────────────────────────────────────
import { useState, useEffect } from "react";
import { Btn } from "./UI";
import { supabase } from "../lib/supabase";

export default function Nav({ page, setPage, user, setUser, staffUser, setStaffUser }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  // Close mobile menu whenever page changes
  useEffect(() => setMenuOpen(false), [page]);

  const navLinks = [
    ["home",     "Home"],
    ["about",    "About"],
    ["programs", "Program"],
    ["faq",      "FAQ"],
    ["teach",    "Become a Teacher"],
  ];

  const AuthButtons = ({ block }) => {
    const btnStyle = block ? { width: "100%", justifyContent: "center" } : {};
    if (staffUser) return (
      <>
        <Btn onClick={() => setPage("staff")} variant="ghost" style={{ ...btnStyle, padding: "7px 16px", fontSize: "0.82rem" }}>
          ⚡ {staffUser.name}
        </Btn>
        <Btn onClick={async () => { await supabase.auth.signOut(); setStaffUser(null); setPage("home"); }} variant="ghost" style={{ ...btnStyle, padding: "7px 14px", fontSize: "0.8rem" }}>
          Sign Out
        </Btn>
      </>
    );
    if (user) return (
      <>
        <Btn onClick={() => setPage("dashboard")} variant="ghost" style={{ ...btnStyle, padding: "7px 16px", fontSize: "0.82rem" }}>
          👋 {(user.name || user.email || "").split(" ")[0]}
        </Btn>
        <Btn onClick={async () => { await supabase.auth.signOut(); setUser(null); setPage("home"); }} variant="ghost" style={{ ...btnStyle, padding: "7px 14px", fontSize: "0.8rem" }}>
          Sign Out
        </Btn>
      </>
    );
    return (
      <>
        <Btn onClick={() => setPage("login")} variant="primary" style={{ ...btnStyle, padding: "8px 18px", fontSize: "0.84rem" }}>
          Sign In / Register
        </Btn>
        <Btn onClick={() => setPage("staff-login")} variant="ghost" style={{ ...btnStyle, padding: "7px 14px", fontSize: "0.8rem", opacity: 0.6 }}>
          ⚡ Staff
        </Btn>
      </>
    );
  };

  return (
    <nav
      style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 200,
        background: scrolled || menuOpen ? "rgba(8,11,38,0.97)" : "transparent",
        backdropFilter: scrolled || menuOpen ? "blur(14px)" : "none",
        borderBottom: scrolled || menuOpen ? "1px solid rgba(255,255,255,0.08)" : "none",
        transition: "background 0.3s",
      }}
    >
      {/* Top bar */}
      <div style={{ padding: "0.85rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem" }}>
        {/* Logo */}
        <button onClick={() => setPage("home")} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <img src="/logo.png" alt="Mars Academy" style={{ height: "clamp(54px, 8vw, 90px)", width: "auto" }} />
        </button>

        {/* Desktop links — hidden on mobile via CSS class */}
        <div className="nav-desktop" style={{ display: "flex", gap: "1.4rem", alignItems: "center" }}>
          {navLinks.map(([p, l]) => (
            <button
              key={p}
              onClick={() => setPage(p)}
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: page === p ? "#FFD93D" : "rgba(255,255,255,0.7)",
                fontFamily: "inherit", fontWeight: 700, fontSize: "0.88rem",
                borderBottom: page === p ? "2px solid #FFD93D" : "2px solid transparent",
                paddingBottom: 2, transition: "color 0.2s", whiteSpace: "nowrap",
              }}
            >
              {l}
            </button>
          ))}
          <AuthButtons />
        </div>

        {/* Hamburger icon — shown on mobile via CSS class */}
        <button
          className="nav-hamburger"
          onClick={() => setMenuOpen((p) => !p)}
          style={{ display: "none", background: "none", border: "none", cursor: "pointer", padding: 6, flexShrink: 0 }}
        >
          <div style={{ width: 22, height: 2, background: "#FFD93D", margin: "4px 0", borderRadius: 2, transition: "all 0.2s", transform: menuOpen ? "rotate(45deg) translate(4px,4px)" : "none" }} />
          <div style={{ width: 22, height: 2, background: "#FFD93D", margin: "4px 0", borderRadius: 2, opacity: menuOpen ? 0 : 1, transition: "all 0.2s" }} />
          <div style={{ width: 22, height: 2, background: "#FFD93D", margin: "4px 0", borderRadius: 2, transition: "all 0.2s", transform: menuOpen ? "rotate(-45deg) translate(4px,-4px)" : "none" }} />
        </button>
      </div>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", padding: "1rem 1.5rem 1.5rem", display: "flex", flexDirection: "column", gap: "0.7rem" }}>
          {navLinks.map(([p, l]) => (
            <button
              key={p}
              onClick={() => setPage(p)}
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: page === p ? "#FFD93D" : "rgba(255,255,255,0.75)",
                fontFamily: "inherit", fontWeight: 700, fontSize: "1rem",
                textAlign: "left", padding: "0.4rem 0",
                borderBottom: "1px solid rgba(255,255,255,0.07)",
              }}
            >
              {l}
            </button>
          ))}
          <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem", marginTop: "0.4rem" }}>
            <AuthButtons block />
          </div>
        </div>
      )}
    </nav>
  );
}
